import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { redirectTo, selectCurrentUser, selectIconPack } from "@shared/store/auth.duck";
import { canEditCustomWhereabouts } from "@shared/utils/AccessControl";
import { PageTitle } from '@shared/ui/atoms/PageTitle';
import TextField from '@shared/ui/atoms/TextField';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import {
  IconFilter,
  WhereaboutsImagePicker
} from '@shared/dialogs/custom-whereabouts-dialog/WhereaboutsImagePicker';
import {
  createUpdateWhereaboutsOption,
  CustomWhereaboutsType,
  deleteWhereaboutsOption,
  FixedWhereabouts,
  FreeBusyStatus,
  WhereaboutsOption,
  WhereaboutsOptionItem
} from "@features/whereabouts/services/WhereaboutOptions";
import { IconFilterRow } from "./components/IconFilterRow";
import {
  WhereaboutsAvailabilityToggle
} from '@shared/dialogs/custom-whereabouts-dialog/WhereaboutsAvailabilityToggle';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { CreatingOfficeContentCard } from "./components/CreatingOfficeContentCard";
import { useParams } from "react-router";
import {
  loadWhereaboutsOptions,
  selectAllWhereaboutsOptions
} from '@features/whereabouts/store/whereaboutsOptions.duck';
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { safeParseInt } from "@shared/utils/MathUtils";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { selectHasGlobalConfigLoaded } from "@shared/store/config.duck";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { AdditionalLeaveOptions } from "./components/AdditionalLeaveOptions";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Colours from '@shared/ui/atoms/Colours';
import { WithCustomWhereaboutsUsageLimitChecker } from "./hooks/WithCustomWhereaboutsUsageLimitChecker";
import { DayNotesSetting } from "./components/DayNotesSetting";
import { OptionListSetting } from "./components/OptionListSetting";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { SchengenTracking } from "./components/SchengenSetting";
import { useTranslation } from "react-i18next";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

function getCustomWhereaboutsType(isLeaveType: boolean): CustomWhereaboutsType {
  if (isLeaveType) {
    return CustomWhereaboutsType.LEAVE;
  } else {
    return CustomWhereaboutsType.CUSTOM;
  }
}

function isDefaultWhereabouts(whereabouts?: WhereaboutsOption): boolean {
  if (!whereabouts) return false;
  return whereabouts.key === FixedWhereabouts.NotWorking ||
    whereabouts.key === FixedWhereabouts.Home ||
    whereabouts.key === FixedWhereabouts.Holiday ||
    whereabouts.key === FixedWhereabouts.Office ||
    whereabouts.key === FixedWhereabouts.CompanyHoliday ||
    whereabouts.key === FixedWhereabouts.Unknown ||
    whereabouts.key === FixedWhereabouts.HolidayApproved ||
    whereabouts.key === FixedWhereabouts.HolidayPending ||
    whereabouts.key === FixedWhereabouts.HolidayRejected;
}

export function AddCustomWhereaboutsPage(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const canEdit = useMemo(() => canEditCustomWhereabouts(currentUser), [currentUser]);
  const iconPack = useSelector(selectIconPack);
  const globalConfigLoaded = useSelector(selectHasGlobalConfigLoaded);
  const allCustomWhereabouts = useSelector(selectAllWhereaboutsOptions);
  const params: any = useParams();
  const [whereaboutsName, setWhereaboutsName] = useState('');
  const [colour, setColour] = useState<undefined | string>(undefined);
  const [image, setImage] = useState<undefined | string>(undefined);
  const [enabled, setEnabled] = useState(true);
  const [iconFilter, setIconFilter] = useState<IconFilter | undefined>(undefined);
  const [freeBusyStatus, setFreeBusyStatus] = useState(FreeBusyStatus.FREE);
  const [loading, setLoading] = useState(false);
  const [isLeaveOption, setLeaveOption] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [dayNotesEnabled, setDayNotesEnabledChanged] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSchengen, setIsSchengen] = useState(false);
  const [optionListEnabled, setOptionListEnabled] = useState(false);
  const [optionItems, setOptionItems] = useState<WhereaboutsOptionItem[]>([]);
  const [optionListFreeTextEnabled, setOptionListFreeTextEnabled] = useState(false);
  const limitReached = WithCustomWhereaboutsUsageLimitChecker();

  const customWhereaboutsId = !!params.customWhereaboutsId ? safeParseInt(params.customWhereaboutsId) : undefined;

  const editingCustomWhereabouts = useMemo(() => {
    if (customWhereaboutsId && globalConfigLoaded) {
      const editingOption = allCustomWhereabouts.find((cw => cw.id === customWhereaboutsId));
      if (editingOption) {
        return editingOption;
      } else {
        dispatch(failureNotification('Could not find custom whereabouts'))
        dispatch(redirectTo('/manage/custom-whereabouts'));
      }
    }
    return undefined;
  }, [customWhereaboutsId, globalConfigLoaded, allCustomWhereabouts, dispatch]);

  const key = useMemo(() => {
    if (editingCustomWhereabouts) {
      return editingCustomWhereabouts.key;
    } else {
      return whereaboutsName.toUpperCase().replace(/[^a-z0-9+]+/gi, '_');
    }
  }, [editingCustomWhereabouts, whereaboutsName]);

  const isEditingDefaultWhereabouts = isDefaultWhereabouts(editingCustomWhereabouts);

  useEffect(() => {
    if (freeBusyStatus === FreeBusyStatus.OOF || freeBusyStatus === FreeBusyStatus.BUSY) setColour('grey');
    else if (freeBusyStatus === FreeBusyStatus.WORKING_ELSEWHERE || freeBusyStatus === FreeBusyStatus.TENTATIVE) setColour('yellow');
    else setColour('green');
  }, [freeBusyStatus]);

  useEffect(() => {
    if (!customWhereaboutsId && limitReached) {
      dispatch(redirectTo('/manage/custom-whereabouts'));
    }
  }, [dispatch, limitReached, customWhereaboutsId]);

  useEffect(() => {
    if (editingCustomWhereabouts) {
      setWhereaboutsName(editingCustomWhereabouts.label);
      setColour(editingCustomWhereabouts.colour);
      setImage(editingCustomWhereabouts.image);
      setEnabled(editingCustomWhereabouts.enabled);
      setFreeBusyStatus(editingCustomWhereabouts.freeBusyStatus);
      setIsPrivate(editingCustomWhereabouts.isPrivate);
      setRequiresApproval(editingCustomWhereabouts.requiresApproval);
      setLeaveOption(editingCustomWhereabouts.whereaboutsType === CustomWhereaboutsType.LEAVE)
      setDayNotesEnabledChanged(editingCustomWhereabouts.dayNotesEnabled);
      setIsSchengen(editingCustomWhereabouts?.schengen ?? false);
      setOptionListEnabled(editingCustomWhereabouts.optionListEnabled ?? false);
      setOptionItems(editingCustomWhereabouts.optionItems ?? []);
      setOptionListFreeTextEnabled(editingCustomWhereabouts.optionListFreeTextEnabled ?? false);
    }
  }, [editingCustomWhereabouts]);

  const onSaveClick = async () => {
    setLoading(true);
    try {
      const whereaboutsOption: WhereaboutsOption = {
        id: editingCustomWhereabouts?.id ?? 0,
        dataTest: key.toLowerCase(),
        alt: whereaboutsName,
        image: image ?? '',
        key: key,
        companyId: currentUser?.companyEntity.id ?? '',
        label: whereaboutsName,
        enabled: enabled,
        colour: colour ?? '',
        freeBusyStatus: freeBusyStatus,
        // Values to setup
        status: key,
        outlookNote: whereaboutsName,
        freeTextEnabled: false,
        // Values defaulted on server
        slackNote: '',
        pickable: editingCustomWhereabouts?.pickable ?? false,
        required: editingCustomWhereabouts?.required ?? false,

        requiresApproval: requiresApproval,
        whereaboutsType: getCustomWhereaboutsType(isLeaveOption),
        isPrivate: isPrivate,
        dayNotesEnabled: dayNotesEnabled,
        schengen: isSchengen,
        optionListEnabled: optionListEnabled,
        optionListFreeTextEnabled: optionListFreeTextEnabled,
        optionItems: optionItems.filter(item => item.label.trim().length > 0),
      }
      await createUpdateWhereaboutsOption(whereaboutsOption);
      await dispatch(loadWhereaboutsOptions(true));
      dispatch(redirectTo('/manage/custom-whereabouts'));
    } catch (e: any) {
      console.error(e);
      dispatch(failureNotification('Failed to create custom option'));
    } finally {
      setLoading(false)
    }
  }

  const onDeleteClicked = async () => {
    if (editingCustomWhereabouts) {
      try {
        setLoading(true);
        await deleteWhereaboutsOption(editingCustomWhereabouts);
        dispatch(successNotification('Custom whereabouts deleted'));
        dispatch(redirectTo('/manage/custom-whereabouts'));
      } catch (e) {
        console.error(e);
        dispatch(failureNotification('Failed to delete custom whereabouts'));
      } finally {
        setLoading(false);
      }
    }
  }

  if (!canEdit) {
    return null;
  }

  return <>
    {(loading || (!globalConfigLoaded && customWhereaboutsId)) && <LoadingSpinner fullScreen={true} hideText={true} />}
    <GoBackButton url={'/manage/custom-whereabouts'} />

    <PageTitle text={'custom-whereabouts-page.create-title'} headlineLarge={true} style={{margin: '0 0 32px 0'}} />

    <BodyRegular style={{marginBottom: 8}} weight={600}>{t('custom-whereabouts-page.step-1-name')}</BodyRegular>
    <TextField onChange={setWhereaboutsName} fullWidth={true} label={''} value={whereaboutsName} disabled={loading} />

    <BodyRegular style={{marginBottom: 8, marginTop: 48}} weight={600}>{t('custom-whereabouts-page.step-2-icon')}</BodyRegular>

    <IconFilterRow onSelect={setIconFilter} selected={iconFilter} disabled={loading} />

    <WhereaboutsImagePicker color={colour}
                            iconPack={iconPack}
                            freeBusyStatus={freeBusyStatus}
                            onSelect={(icon: string) => setImage(icon)}
                            selectedImage={image}
                            iconFilter={iconFilter}
                            disabled={loading} />

    <BodyRegular style={{marginBottom: 8, marginTop: 48}} weight={600}>{t('custom-whereabouts-page.step-3-availability')}</BodyRegular>

    <WhereaboutsAvailabilityToggle onSelect={(val: FreeBusyStatus) => setFreeBusyStatus(val)}
                                   disabled={loading}
                                   selected={freeBusyStatus} />

    <HeadlineMedium style={{marginTop: 48, marginBottom: 24}}>{t('custom-whereabouts-page.final-setup')}</HeadlineMedium>
    {!isEditingDefaultWhereabouts && <>
      <AdditionalLeaveOptions isLeaveOption={isLeaveOption} onIsLeaveOptionChange={setLeaveOption}
                              requiresApproval={requiresApproval} onRequiresApprovalChange={setRequiresApproval}
                              isPrivate={isPrivate} onIsPrivateChange={setIsPrivate} />
    </>}

      <DayNotesSetting dayNotesEnabled={dayNotesEnabled} onDayNotesEnabledChanged={setDayNotesEnabledChanged} />

    <OptionListSetting optionListEnabled={optionListEnabled}
                       onOptionListEnabledChanged={setOptionListEnabled}
                       optionItems={optionItems}
                       onOptionItemsChanged={setOptionItems}
                       freeTextEnabled={optionListFreeTextEnabled}
                       onFreeTextEnabledChanged={setOptionListFreeTextEnabled}
                       disabled={loading} />

    {!isEditingDefaultWhereabouts && <>
      <SchengenTracking schengenEnabled={isSchengen} onSchengenEnabledChanged={setIsSchengen} />
    </>}

    <CreatingOfficeContentCard style={{marginTop: 32}} />

    <PrimaryButton click={onSaveClick}
                   text={"button.save"}
                   fullWidth={true}
                   size={ButtonSize.Large}
                   dataTest={'save-custom-whereabouts-button'}
                   style={{marginBottom: 16, marginTop: 48}}
                   disabled={loading} />

    {editingCustomWhereabouts && <OutlineButton icon={IconTypes.Bin}
                                                text={'custom-whereabouts-page.delete-whereabouts'}
                                                style={{margin: '32px auto 0 auto'}}
                                                hideBorder={true}
                                                size={ButtonSize.Large}
                                                click={onDeleteClicked} borderColour={Colours.darkGrey}
                                                disabled={loading} />}
  </>;
}

interface Props {
}
